// Guess in an online room: every player on their own phone (a TV can show the pictures).
// The server runs the chess clock; this device listens only on its own player's turn and sends
// the utterance with the exact end-of-speech time (in server time).
import type { GuessFeedback, GuessView } from "@shared/games/guess";
import type { GuessPicture, VoiceProfileInfo } from "@shared/guess/api";
import { remainingAt, type ClockState } from "@shared/guess/clock";
import type { RoomView } from "@shared/types";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Avatar } from "../../components/Avatar";
import { Btn } from "../../components/Button";
import { Check, LogOut, Mic, Pause, Play, RotateCcw, Shield, X } from "../../components/Icon";
import { Sheet } from "../../components/Sheet";
import { toast } from "../../components/Toast";
import { fmt, useI18n } from "../../i18n";
import { api, ApiError, errorCode } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import type { RoomClient } from "../../lib/room-client";
import { haptic, play } from "../../lib/sfx";
import { useAct, useGameAct } from "../common/useRoom";
import { BrowserListener, browserRecognitionAvailable, ServerListener, type Listener, type Utterance } from "./audio/listener";
import { MicError } from "./audio/mic";
import { guessBackend, type GuessConfig } from "./backend";
import type { Feedback, MicState, ReviewEntry } from "./controller";
import { FeedbackOverlay, SmallFeedback } from "./Duel";
import { useGuessDict, type GuessDict } from "./i18n";
import { Results } from "./Results";
import { ClockPill, MicMeter, PictureCard, useFrameNow, useMedia } from "./ui";
import { VoiceCheck, type VoicePass } from "./VoiceCheck";

// ------------------------------------------------------------------ microphone for this device

interface RoomMic {
  config: GuessConfig | null;
  profile: VoiceProfileInfo | null;
  listener: Listener | null;
  error: string | null;
  busy: boolean;
  open(lang: "ka" | "en"): Promise<void>;
}

/** One microphone per device for the whole room session (voice check → every match → rematches). */
function useRoomMic(): RoomMic {
  const { user } = useAuth();
  const t = useGuessDict();
  const [config, setConfig] = useState<GuessConfig | null>(null);
  const [profile, setProfile] = useState<VoiceProfileInfo | null>(null);
  const [listener, setListener] = useState<Listener | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef<Listener | null>(null);
  ref.current = listener;

  useEffect(() => {
    guessBackend
      .config()
      .then(setConfig)
      .catch(() => setConfig({ stt: { mode: "browser", provider: "browser", langs: ["ka-GE", "en-US"] }, samplesAvailable: false, sampleDays: 0 }));
  }, []);
  useEffect(() => {
    if (!user) return;
    api<{ profile: VoiceProfileInfo | null }>("/guess/voice-profile")
      .then((r) => setProfile(r.profile))
      .catch(() => setProfile(null));
  }, [user]);
  useEffect(() => () => ref.current?.close(), []);

  const open = useCallback(
    async (lang: "ka" | "en") => {
      if (!config || ref.current) return;
      setBusy(true);
      setError(null);
      try {
        if (config.stt.mode === "browser") {
          if (!browserRecognitionAvailable()) throw new MicError("unsupported");
          const s = await navigator.mediaDevices.getUserMedia({ audio: true });
          s.getTracks().forEach((tr) => tr.stop());
          setListener(new BrowserListener(lang === "ka" ? "ka-GE" : "en-US"));
        } else {
          setListener(await ServerListener.open(profile?.calibration ?? null));
        }
        play("pop");
      } catch (e) {
        const code = e instanceof MicError ? e.code : (e as DOMException)?.name === "NotAllowedError" ? "denied" : "missing";
        setError(code === "denied" ? t.micDenied : code === "insecure" ? t.micInsecure : code === "unsupported" ? t.sttUnavailable : t.micMissing);
      } finally {
        setBusy(false);
      }
    },
    [config, profile, t],
  );

  return { config, profile, listener, error, busy, open };
}

// ------------------------------------------------------------------ the room view

export function RoomGuess({ client, view, tv }: { client: RoomClient; view: RoomView; tv?: boolean }) {
  const g = view.view as GuessView;
  const mic = useRoomMic();
  usePreload(g.window);
  if (g.stage === "check") return <CheckStage client={client} view={view} g={g} mic={mic} tv={tv} />;
  if (g.stage === "intro") return <IntroStage client={client} g={g} paused={view.paused} />;
  if (g.stage === "play") return <PlayStage client={client} view={view} g={g} mic={mic} tv={tv} />;
  return <OverStage view={view} client={client} g={g} />;
}

const preloaded = new Set<string>();
function usePreload(pictures: GuessPicture[]) {
  useEffect(() => {
    for (const p of pictures) {
      if (!p.src || preloaded.has(p.src)) continue;
      preloaded.add(p.src);
      const img = new Image();
      img.decoding = "async";
      img.src = p.src;
    }
  }, [pictures]);
}

// ------------------------------------------------------------------ voice check (each on their own phone)

function CheckStage({ client, view, g, mic, tv }: { client: RoomClient; view: RoomView; g: GuessView; mic: RoomMic; tv?: boolean }) {
  const t = useGuessDict();
  const { lang } = useI18n();
  const { user } = useAuth();
  const act = useAct(client);
  const gact = useGameAct(client);
  const [attempt, setAttempt] = useState(0);
  const me = g.mySeat >= 0 ? g.seats[g.mySeat] : null;
  const waiting = g.seats.filter((s, i) => !s.checked && s.present && !g.clock.players[i]?.out);

  const onPassed = useCallback(
    async (p: VoicePass) => {
      const r = await gact(p.viaProfile ? { t: "voiceOk", profile: true } : { t: "voiceOk", testId: p.testId });
      if (!r.ok) setAttempt((n) => n + 1); // start over with a fresh test
      else haptic(20);
    },
    [gact],
  );

  return (
    <div className="shell max-w-3xl py-6 sm:py-10">
      <div className="text-center">
        <p className="font-extrabold text-guess">{g.category.name}</p>
        <h1 className="display mt-1 text-[clamp(30px,6vw,48px)]">{t.online.checkTitle}</h1>
        <p className="mx-auto mt-3 max-w-xl text-lg text-ink-2">{t.online.checkSub}</p>
      </div>

      {/* everyone's status */}
      <ul className={`mx-auto mt-6 grid max-w-2xl gap-2 ${g.seats.length > 2 ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2"}`}>
        {g.seats.map((s, i) => {
          const out = !s.present || g.clock.players[i]?.out;
          return (
            <li key={s.id} className="surface-2 flex items-center gap-3 p-3" style={{ opacity: out ? 0.5 : 1 }}>
              <Avatar index={s.avatar} size={44} mood={s.checked ? "happy" : "idle"} disc={false} />
              <div className="min-w-0">
                <div className="truncate font-extrabold">
                  {s.name}
                  {i === g.mySeat && <span className="ml-1 text-sm text-ink-3">· {t.you}</span>}
                </div>
                <div className={`text-sm font-bold ${s.checked ? "text-good" : "text-ink-3"}`}>
                  {out ? t.online.checkLeft : s.checked ? (
                    <span className="inline-flex items-center gap-1">
                      <Check size={14} /> {t.online.checkReady}
                    </span>
                  ) : (
                    t.online.checkPending
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="mt-8">
        {me && !me.checked && !tv ? (
          !mic.listener ? (
            <MicCard mic={mic} t={t} lang={lang === "ka" ? "ka" : "en"} />
          ) : mic.config ? (
            <VoiceCheck
              key={attempt}
              player={{ name: me.name, avatar: me.avatar }}
              listener={mic.listener}
              backend={guessBackend}
              config={mic.config}
              isOwner={!!user}
              profile={user ? mic.profile : null}
              onPassed={onPassed}
            />
          ) : null
        ) : (
          <div className="surface mx-auto max-w-2xl p-6 text-center sm:p-8">
            <p className="text-lg font-bold text-ink-2">{me ? t.online.youPassed : t.online.spectator}</p>
            {waiting.length > 0 && <p className="mt-2 text-ink-3">{fmt(t.online.waitingFor, { names: waiting.map((w) => w.name).join(", ") })}</p>}
            <p className="display mt-6 text-[clamp(20px,4vw,28px)] leading-snug">{t.rulesText}</p>
            <ul className="mt-5 grid gap-2 text-left">
              {t.rulesBullets.map((b, i) => (
                <li key={i} className="flex gap-3 text-ink-2">
                  <Check size={20} className="mt-0.5 shrink-0 text-good" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {view.me?.isHost && (
        <div className="mt-8 text-center">
          <Btn variant="ghost" icon={<LogOut size={18} />} onClick={() => void act({ t: "lobby" })}>
            {t.online.toLobby}
          </Btn>
        </div>
      )}
    </div>
  );
}

function MicCard({ mic, t, lang }: { mic: RoomMic; t: GuessDict; lang: "ka" | "en" }) {
  const cfg = mic.config;
  const browser = cfg?.stt.mode === "browser";
  return (
    <div className="surface mx-auto grid max-w-xl place-items-center gap-5 p-8 text-center">
      <span className="grid h-20 w-20 place-items-center rounded-full bg-guess text-[#2a0f00]" style={{ boxShadow: "0 8px 0 var(--color-guess-deep)" }}>
        <Mic size={38} />
      </span>
      <p className="text-lg text-ink-2">{t.micWhy}</p>
      <Btn game="guess" size="lg" onClick={() => void mic.open(lang)} disabled={!cfg || mic.busy}>
        {t.micAllow}
      </Btn>
      {mic.error && <p className="text-bad">{mic.error}</p>}
      {cfg && (
        <p className="flex max-w-md items-start gap-2 text-left text-sm text-ink-3">
          <Shield size={18} className="mt-0.5 shrink-0" />
          {browser ? t.privacyBrowser : fmt(t.privacyServer, { provider: cfg.stt.provider === "google" ? "Google Cloud" : cfg.stt.provider === "azure" ? "Microsoft Azure" : cfg.stt.provider })}
        </p>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ 3-2-1 (in server time, the same on every phone)

function IntroStage({ client, g, paused }: { client: RoomClient; g: GuessView; paused: boolean }) {
  const t = useGuessDict();
  useFrameNow(!paused);
  const elapsed = paused ? 0 : Date.now() + client.offset() - (g.introAt ?? 0);
  // 0.4 s "ready", then 3 · 2 · 1 (0.8 s each), then GO
  const step = elapsed < 400 ? null : elapsed < 1200 ? 3 : elapsed < 2000 ? 2 : elapsed < 2800 ? 1 : 0;
  const last = useRef<number | null>(null);
  useEffect(() => {
    if (step === null || step === last.current) return;
    last.current = step;
    play(step === 0 ? "go" : "count");
  }, [step]);
  return (
    <div data-guess-stage="intro" className="fixed inset-0 z-[45] grid place-items-center px-6 text-center" style={{ background: "color-mix(in oklab, var(--color-guess) 9%, var(--color-ground))" }}>
      <div>
        <p className="text-xl font-extrabold text-ink-2">{g.category.name}</p>
        <AnimatePresence mode="popLayout">
          <motion.div
            key={String(step)}
            initial={{ scale: 0.4, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 1.6, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 18 }}
            className="display mt-4 leading-none text-guess"
            style={{ fontSize: step === 0 || step === null ? "min(16vw,120px)" : "min(42vw,260px)" }}
          >
            {paused ? <span className="text-[min(14vw,96px)]">{t.pausedTitle}</span> : step === null ? "" : step === 0 ? t.countdownGo : step}
          </motion.div>
        </AnimatePresence>
        <p className="mx-auto mt-6 max-w-md text-lg font-bold text-ink-2">{t.rulesText}</p>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ the match

function asFeedback(f: GuessFeedback | null): Feedback | null {
  return f ? { id: f.id, kind: f.kind, player: f.seat, heard: f.heard, reason: f.reason, answer: f.answer, penalty: f.penalty } : null;
}

function PlayStage({ client, view, g, mic, tv }: { client: RoomClient; view: RoomView; g: GuessView; mic: RoomMic; tv?: boolean }) {
  const t = useGuessDict();
  const { lang } = useI18n();
  const act = useAct(client);
  const gact = useGameAct(client);
  const clock = g.clock as ClockState;
  const running = clock.phase === "running" && !view.paused;
  useFrameNow(running);
  const now = Date.now() + client.offset();
  const me = g.mySeat;
  const seated = me >= 0 && !tv;
  const myTurn = seated && clock.active === me && running && g.current !== null;
  const many = g.seats.length > 2;
  const side = useMedia("(min-width: 900px) and (min-aspect-ratio: 5/4)") && !many;
  const [level, setLevel] = useState(-140);
  const [hearing, setHearing] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmQuit, setConfirmQuit] = useState(false);
  const myTurnRef = useRef(myTurn);
  myTurnRef.current = myTurn;
  const lastSpeaking = useRef(0);
  const picture = g.current !== null ? g.window.find((p) => p.i === g.current) ?? null : null;
  const fb = asFeedback(g.feedback);
  const players = g.seats.map((s) => ({ name: s.name, avatar: s.avatar }));

  // the microphone listens only on this player's turn
  const listener = seated ? mic.listener : null;
  useEffect(() => {
    listener?.setActive(myTurn);
    if (!myTurn) setHearing(false);
  }, [listener, myTurn]);
  useEffect(() => {
    const l = seated ? g.seats[me]?.lang : null;
    if (listener?.setLang && l) listener.setLang(l === "ka" ? "ka-GE" : "en-US");
  }, [listener, seated, me, g.seats]);

  const send = useCallback(
    async (u: Utterance) => {
      const auth = client.seatAuth?.();
      if (!auth) return;
      // the end of speech in server time (the clock stops there, not when the judge answers)
      const at = Math.round(Date.now() + client.offset() - Math.max(0, performance.now() - u.endAt));
      const q = new URLSearchParams({ pid: auth.playerId, u: u.id, ms: String(Math.round(u.speechMs)), at: String(at) });
      const path = `/rooms/${client.code}/guess/utterance?${q}`;
      const headers = { "x-player-token": auth.token };
      setSending(true);
      try {
        for (let attempt = 0; attempt < 2; attempt++) {
          try {
            if (u.wav) await api(path, { raw: u.wav, headers, timeoutMs: 20_000 });
            else await api(path, { body: { transcripts: u.transcripts ?? [] }, headers, timeoutMs: 20_000 });
            break;
          } catch (e) {
            // a request that never arrived is sent once more (the server judges each utterance once)
            if (attempt === 0 && !(e instanceof ApiError && e.status > 0)) continue;
            const c = errorCode(e);
            if (c !== "stale" && c !== "paused") toast(t.online.sendFailed, "bad");
            break;
          }
        }
      } finally {
        setSending(false);
      }
    },
    [client, t],
  );

  useEffect(() => {
    if (!listener) return;
    const offs = [
      listener.on("level", (db) => setLevel(db)),
      listener.on("start", () => {
        if (!myTurnRef.current) return;
        setHearing(true);
        // tell the room we are talking, so a clock reaching zero mid-sentence waits for the answer
        const n = performance.now();
        if (n - lastSpeaking.current > 1500) {
          lastSpeaking.current = n;
          void gact({ t: "speaking" }, true);
        }
      }),
      listener.on("drop", () => setHearing(false)),
      listener.on("end", (u) => {
        setHearing(false);
        if (myTurnRef.current) void send(u);
      }),
      listener.on("error", (code) => {
        if (code === "denied") toast(t.micDenied, "bad");
      }),
    ];
    return () => offs.forEach((o) => o());
  }, [listener, gact, send, t]);

  // sounds and haptics for every verdict; quiet ones only on the answering phone (other phones' sounds
  // must not reach the answering microphone while its clock runs)
  const lastFb = useRef<number | null>(g.feedback?.id ?? null);
  useEffect(() => {
    const f = g.feedback;
    if (!f || f.id === lastFb.current) return;
    lastFb.current = f.id;
    const mine = seated && f.seat === me;
    switch (f.kind) {
      case "correct":
        play("correct");
        if (mine) haptic(18);
        break;
      case "wrong":
        play("wrong");
        if (mine) haptic([30, 40, 30]);
        break;
      case "skip":
        play("skip");
        break;
      case "out":
        play("timeup");
        if (mine) haptic([40, 60, 40]);
        break;
      case "unclear":
      case "ignored":
        if (mine) play("tock", { volume: 0.5 });
        break;
      case "error":
        if (mine) play("bleep", { volume: 0.6 });
        break;
    }
  }, [g.feedback, seated, me]);

  // a little nudge when the turn comes to this phone
  const wasMine = useRef(false);
  useEffect(() => {
    if (myTurn && !wasMine.current) haptic(25);
    wasMine.current = myTurn;
  }, [myTurn]);

  const activeMs = remainingAt(clock, clock.active, now);
  const lastSeconds = running && activeMs <= 10_000;
  const activeName = g.seats[clock.active]?.name ?? "";
  const technical = clock.phase === "paused" && clock.pauseKind === "technical";
  const canResume = view.me?.isHost || (seated && clock.active === me);
  const penaltyKey = fb && fb.penalty ? fb.id : null;
  const micState: MicState = !myTurn && !(seated && clock.active === me && clock.phase === "checking") ? "off" : clock.phase === "checking" || sending ? "checking" : hearing ? "hearing" : "listening";
  const micLabel = micState === "checking" ? t.checking : micState === "hearing" ? t.hearing : micState === "listening" ? t.listening : "";

  const clocks = g.seats.map((s, i) => (
    <ClockPill
      tall={side}
      key={s.id}
      name={s.name}
      avatar={s.avatar}
      ms={remainingAt(clock, i, now)}
      active={i === clock.active}
      out={clock.players[i]?.out ?? false}
      compact={many}
      penaltyKey={penaltyKey && fb?.player === i ? penaltyKey : null}
      mood={fb && fb.player === i ? (fb.kind === "correct" ? "happy" : fb.kind === "wrong" || fb.kind === "out" ? "sad" : undefined) : undefined}
      align={!many && i === 1 ? "right" : "left"}
    />
  ));

  return (
    <div
      data-guess-stage="play"
      className="fixed inset-0 z-[45] flex flex-col overflow-hidden"
      style={{ background: `color-mix(in oklab, ${lastSeconds ? "var(--color-bad)" : "var(--color-guess)"} ${lastSeconds ? 12 : 7}%, var(--color-ground))`, transition: "background .4s" }}
    >
      {/* top bar: the host can pause or call the match off */}
      <div className="flex items-center justify-between gap-3 px-4 pt-[max(12px,env(safe-area-inset-top))]">
        {view.me?.isHost ? (
          <button type="button" className="icon-btn" aria-label={t.pausedTitle} onClick={() => void act({ t: "pause" })} disabled={view.paused}>
            <Pause size={20} />
          </button>
        ) : (
          <span className="w-11" />
        )}
        <div className="min-w-0 truncate text-center font-extrabold text-ink-2">{g.category.name}</div>
        {view.me?.isHost ? (
          <button type="button" className="icon-btn" aria-label={t.online.callOff} onClick={() => setConfirmQuit(true)}>
            <X size={20} />
          </button>
        ) : (
          <span className="w-11" />
        )}
      </div>

      {!side && <div className={`mx-auto mt-3 grid w-full max-w-[980px] gap-3 px-4 ${many ? "grid-cols-3" : "grid-cols-2"}`}>{clocks}</div>}

      {/* whose turn */}
      <div className="mt-3 min-h-7 px-4 text-center font-extrabold" aria-live="polite">
        {clock.phase !== "over" && (
          <motion.span key={`${clock.active}-${myTurn}`} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className={lastSeconds ? "text-bad" : "text-guess"}>
            {myTurn || (seated && clock.active === me) ? (lastSeconds ? t.lastSeconds : t.online.yourTurn) : fmt(t.turnOf, { name: activeName })}
          </motion.span>
        )}
      </div>

      {/* the picture */}
      <div className={`relative flex min-h-0 flex-1 items-center justify-center px-4 py-3 ${side ? "gap-[4vw]" : ""}`}>
        {side && <div className="flex flex-1 justify-end">{clocks[0]}</div>}
        <div className="relative">
          <AnimatePresence mode="popLayout">
            <motion.div
              key={picture ? `p${picture.i}` : "empty"}
              initial={{ opacity: 0, scale: 0.86, rotate: -2 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              exit={{ opacity: 0, scale: 1.06 }}
              transition={{ type: "spring", stiffness: 260, damping: 24 }}
            >
              <PictureCard
                picture={picture}
                size={side ? "min(44vw, calc(100dvh - 220px), 720px)" : `min(88vw, calc(100dvh - ${seated ? 330 : 280}px), 620px)`}
                hearing={micState === "hearing"}
              />
            </motion.div>
          </AnimatePresence>
          <FeedbackOverlay fb={fb} t={t} players={players} />
        </div>
        {side && <div className="flex flex-1 justify-start">{clocks[1]}</div>}
      </div>

      {/* this phone's microphone (only the answering player's counts) */}
      <div className="px-4 pb-[max(16px,env(safe-area-inset-bottom))]">
        {/* "say it again" is for the answering player; everyone sees what counted */}
        <SmallFeedback fb={fb && (fb.kind === "unclear" || fb.kind === "ignored") && !(seated && fb.player === me) ? null : fb} t={t} />
        {seated && (
          <div className="mt-2">
            {mic.listener ? (
              clock.active === me ? (
                <MicMeter level={level} state={micState} label={micLabel} />
              ) : (
                <p className="text-center text-sm font-bold text-ink-3">{fmt(t.online.quiet, { name: activeName })}</p>
              )
            ) : (
              <div className="flex justify-center">
                <Btn game="guess" icon={<Mic size={18} />} onClick={() => void mic.open(g.seats[me]?.lang ?? (lang === "ka" ? "ka" : "en"))} disabled={!mic.config || mic.busy}>
                  {t.micAllow}
                </Btn>
              </div>
            )}
            {mic.error && <p className="mt-1 text-center text-sm text-bad">{mic.error}</p>}
          </div>
        )}
      </div>

      {/* the host paused the room: clocks stopped, picture hidden */}
      <AnimatePresence>
        {view.paused && (
          <motion.div className="absolute inset-0 z-40 grid place-items-center bg-[rgba(26,10,49,.92)] px-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="w-full max-w-sm text-center">
              <Pause size={52} className="mx-auto text-guess" />
              <h2 className="display mt-4 text-4xl">{t.pausedTitle}</h2>
              <p className="mt-3 text-lg text-ink-2">{t.pausedText}</p>
              {view.me?.isHost ? (
                <Btn className="mt-8" game="guess" size="lg" block icon={<Play size={20} />} onClick={() => void act({ t: "resume" })}>
                  {t.resume}
                </Btn>
              ) : (
                <p className="mt-6 text-ink-3">{t.online.waitHost}</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* technical pause: the recogniser failed; nobody loses time */}
      <AnimatePresence>
        {technical && !view.paused && (
          <motion.div className="absolute inset-0 z-40 grid place-items-center bg-[rgba(26,10,49,.9)] px-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="w-full max-w-sm text-center">
              <h2 className="display text-4xl">{t.techPause}</h2>
              <p className="mt-3 text-lg text-ink-2">{t.techPauseText}</p>
              {canResume ? (
                <Btn className="mt-8" game="guess" size="lg" block icon={<Play size={20} />} onClick={() => void gact({ t: "resume" })}>
                  {t.resume}
                </Btn>
              ) : (
                <p className="mt-6 text-ink-3">{t.online.resumeHint}</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Sheet open={confirmQuit} onClose={() => setConfirmQuit(false)} size="narrow" title={t.online.callOff}>
        <p className="text-lg text-ink-2">{t.online.callOffConfirm}</p>
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <Btn variant="soft" onClick={() => setConfirmQuit(false)}>
            {t.back}
          </Btn>
          <Btn
            onClick={() => {
              setConfirmQuit(false);
              void gact({ t: "quit" });
            }}
          >
            {t.online.callOff}
          </Btn>
        </div>
      </Sheet>
    </div>
  );
}

// ------------------------------------------------------------------ results

function OverStage({ view, client, g }: { view: RoomView; client: RoomClient; g: GuessView }) {
  const t = useGuessDict();
  const { user } = useAuth();
  const act = useAct(client);
  const host = !!view.me?.isHost;
  const reviews: ReviewEntry[] = useMemo(() => {
    const byIndex = new Map(g.window.map((p) => [p.i, p]));
    return g.log.map((e, k) => ({ utteranceId: `r${k}`, player: e.seat, picture: byIndex.get(e.image) ?? null, verdict: e.verdict, reason: e.reason, heard: e.heard, answer: e.answer }));
  }, [g.log, g.window]);
  const actions = host ? (
    <>
      <Btn game="guess" size="lg" icon={<RotateCcw size={20} />} onClick={() => void act({ t: "again" })}>
        {t.rematch}
      </Btn>
      <Btn variant="soft" size="lg" onClick={() => void act({ t: "lobby" })}>
        {t.changeCategory}
      </Btn>
    </>
  ) : (
    <p className="text-center text-lg font-bold text-ink-3">{t.online.waitHost}</p>
  );

  if (!g.result) {
    return (
      <div className="shell grid min-h-[60dvh] place-items-center py-10 text-center">
        <div>
          <Avatar index={g.seats[0]?.avatar ?? 0} size={110} mood="sad" disc={false} className="mx-auto" />
          <h1 className="display mt-5 text-4xl">{t.online.calledOff}</h1>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">{actions}</div>
        </div>
      </div>
    );
  }
  return (
    <Results
      result={g.result}
      status="saved"
      verified={g.verified}
      reviews={reviews}
      signedIn={!!user}
      meIndex={g.mySeat >= 0 ? g.mySeat : null}
      actions={actions}
      onRematch={() => void act({ t: "again" })}
      onCategory={() => void act({ t: "lobby" })}
      onRetrySave={() => undefined}
    />
  );
}
